import { useCallback } from 'react'
import { useSubmitEnrollmentStep, useUpdateEnrollmentEndorsements } from '@/api/enrollment-api'
import { ENROLLMENT_COMMON_MESSAGES } from '../../../constants/enrollmentContent'
import type { EligibilityFormData } from '../../../components/eligibilitySteps/types'
import type {
  ApplyHydrationFn,
  FlowSessionState,
  FlowStepNavigation,
  FlowStepState,
  StepActionSetters
} from '../types'
import { useEnrollmentBackAction } from './stepActions/useEnrollmentBackAction'
import { useEnrollmentFinalization } from './stepActions/useEnrollmentFinalization'
import { useEnrollmentJourneyContinueHandlers } from './stepActions/useEnrollmentJourneyContinueHandlers'
import { useEnrollmentProfileContinueHandlers } from './stepActions/useEnrollmentProfileContinueHandlers'
import { useStepSubmission } from './useStepSubmission'

type UseStepActionsArgs = {
  stepState: FlowStepState
  formData: EligibilityFormData
  sessionState: Pick<FlowSessionState, 'enrollmentSessionToken'>
  navigation: FlowStepNavigation
  hydration: {
    applyHydration: ApplyHydrationFn
  }
  setters: StepActionSetters
}

export function useStepActions({
  stepState,
  formData,
  sessionState,
  navigation,
  hydration,
  setters
}: UseStepActionsArgs) {
  const { currentStep, totalSteps, limitToStep } = stepState
  const { enrollmentSessionToken } = sessionState
  const {
    navigate,
    goToStep,
    scrollToTop,
    resetAnonymousEnrollmentAndReturnToOptions,
    suppressNextSessionStepSync,
    setSessionStepSyncLock,
    setUploadDocumentsSkipped,
    consumeUploadDocumentsSkipped
  } =
    navigation
  const { applyHydration } = hydration
  const { setStepSubmissionError, setStepValidationError, setIsCheckingStepAccess } = setters

  const submitEnrollmentStepMutation = useSubmitEnrollmentStep()
  const updateEnrollmentEndorsementsMutation = useUpdateEnrollmentEndorsements()

  const { handleSubmitStep, handleEnglishAssessmentPassed, handleNext } = useStepSubmission({
    currentStep,
    totalSteps,
    enrollmentSessionToken,
    navigation: {
      navigate,
      goToStep,
      scrollToTop,
      suppressNextSessionStepSync
    },
    applyHydration,
    setStepSubmissionError,
    setStepValidationError,
    setIsCheckingStepAccess,
    submitEnrollmentStepMutation
  })

  const setDefaultStepSaveError = useCallback(
    (error: unknown) => {
      setStepSubmissionError(
        error instanceof Error ? error.message : ENROLLMENT_COMMON_MESSAGES.saveStepFailed
      )
    },
    [setStepSubmissionError]
  )

  const {
    handleContactDetailsContinue,
    handleEligibilityContinue,
    handleLicenseDetailsContinue
  } = useEnrollmentProfileContinueHandlers({
    formData,
    limitToStep,
    handleSubmitStep,
    navigate,
    setDefaultStepSaveError,
    setStepSubmissionError
  })

  const {
    handleAdditionalInfoContinue,
    handleAdditionalInfoContinueWithoutEndorsements,
    handleDocumentsContinue,
    handleLocationSelectionContinue,
    handleTrainingDateContinue
  } = useEnrollmentJourneyContinueHandlers({
    enrollmentSessionToken,
    formData,
    handleSubmitStep,
    navigate,
    setUploadDocumentsSkipped,
    setDefaultStepSaveError,
    setStepSubmissionError,
    updateEnrollmentEndorsementsMutation
  })

  const handleBack = useEnrollmentBackAction({
    consumeUploadDocumentsSkipped,
    currentStep,
    enrollmentSessionToken,
    goToStep,
    resetAnonymousEnrollmentAndReturnToOptions,
    setSessionStepSyncLock,
    suppressNextSessionStepSync
  })

  const { handleFinalizeEnrollment, isFinalizingEnrollment } = useEnrollmentFinalization({
    enrollmentSessionToken,
    applyHydration,
    goToStep,
    navigate,
    setStepSubmissionError
  })

  return {
    submitEnrollmentStepMutation,
    updateEnrollmentEndorsementsMutation,
    handleSubmitStep,
    handleEnglishAssessmentPassed,
    handleNext,
    handleBack,
    handleAdditionalInfoContinue,
    handleAdditionalInfoContinueWithoutEndorsements,
    handleEligibilityContinue,
    handleLicenseDetailsContinue,
    handleContactDetailsContinue,
    handleLocationSelectionContinue,
    handleTrainingDateContinue,
    handleDocumentsContinue,
    handleFinalizeEnrollment,
    isFinalizingEnrollment
  }
}
